import { Paths, ROUTES, type AppRoutePath } from '@shared/AppRoutes';
import LanguageIcon from '@mui/icons-material/Language';
import { type IntlShape } from 'react-intl';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LightbulbOutlineIcon from '@mui/icons-material/LightbulbOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Face4Icon from '@mui/icons-material/Face4';
// import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import PhotoCameraFrontOutlinedIcon from '@mui/icons-material/PhotoCameraFrontOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import CalculateIcon from '@mui/icons-material/Calculate';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { Fragment, type ReactNode } from 'react';
import type { Location } from 'react-router-dom';
import SideMenuButton from './SideMenuButton';
import { Badge, Divider } from '@mui/material';
import { SubscriptionPlans } from '@features/subscription/entity';

export const SideMenuList = (props: {
  items: MenuItem[];
  onClose: () => void;
  location: Location;
}) => {
  const { items: menuItems, onClose, location } = props;

  return (
    <>
      {menuItems.map((menuItem, index) => {
        return !menuItem.condition || menuItem.condition() ? (
          <Fragment key={menuItem.key}>
            {[2, 12].includes(index) ? (
              <Divider sx={{ border: '1px solid rgba(1,1,1,0.2)' }} />
            ) : null}
            <SideMenuButton
              id={menuItem.id}
              key={menuItem.key}
              isActive={location.pathname === menuItem.path}
              to={menuItem.path}
              title={menuItem.title}
              icon={
                menuItem.badgeCount ? (
                  <Badge badgeContent={menuItem.badgeCount} color="error">
                    {menuItem.icon}
                  </Badge>
                ) : (
                  menuItem.icon
                )
              }
              onClick={() => {
                onClose();
                menuItem.onClick?.();
              }}
            />
          </Fragment>
        ) : null;
      })}
    </>
  );
};

export type MenuItem = {
  key: string;
  id?: string;
  title: string;
  path: AppRoutePath | undefined;
  icon: ReactNode;
  condition?: () => boolean;
  onClick?: () => void;
  hasFeatureAccess: boolean;
  badgeCount?: number;
};

export const getMenuItems = (
  userId: string | undefined,
  intl: IntlShape,
  plan: SubscriptionPlans | undefined,
  isEnabledForUsers: boolean,
  teamMember?: { teamId: string },
  stocks?: { id: string }[],
  filters?: { language: string },
  toggleLanguage?: () => void,
  logout?: () => void,
  pendingReservationsCount?: number,
): MenuItem[] => [
  {
    key: 'profile',
    title: intl.formatMessage({ id: 'myProfile', defaultMessage: 'Můj profil' }),
    path: ROUTES.profile.path,
    hasFeatureAccess: plan ? ROUTES.profile.allowedSubscriptionPlans.includes(plan) : false,
    icon: <Face4Icon />,
    id: 'side-menu-profile-button',
  },
  {
    key: 'team',
    title: intl.formatMessage({ id: 'team', defaultMessage: 'Tým' }),
    path: teamMember?.teamId ? Paths.team(teamMember.teamId) : '#',
    icon: <Face4Icon />,
    id: 'side-menu-team-button',
    hasFeatureAccess: plan ? ROUTES.team.allowedSubscriptionPlans.includes(plan) : false,
    condition: () => !!teamMember,
  },
  {
    key: 'dashboard',
    hasFeatureAccess: plan ? ROUTES.home.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'dashboard', defaultMessage: 'Přehledy' }),
    path: ROUTES.home.path,
    icon: <DashboardIcon />,
  },
  {
    key: 'clients',
    hasFeatureAccess: plan ? ROUTES.clients.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'clients', defaultMessage: 'Klienti' }),
    path: ROUTES.clients.path,
    icon: <Face4Icon />,
  },
  {
    key: 'pendingReservations',
    hasFeatureAccess: plan
      ? ROUTES.pendingReservations.allowedSubscriptionPlans.includes(plan)
      : false,
    title: intl.formatMessage({ id: 'pendingReservations', defaultMessage: 'Žádosti o rezervaci' }),
    path: ROUTES.pendingReservations.path,
    icon: <EventNoteIcon />,
    badgeCount: pendingReservationsCount,
  },
  {
    key: 'visits',
    hasFeatureAccess: plan ? ROUTES.visits.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'visitsList', defaultMessage: 'Návštěvy' }),
    path: ROUTES.visits.path,
    icon: <PhotoCameraFrontOutlinedIcon />,
  },
  {
    key: 'receivedPayments',
    hasFeatureAccess: plan
      ? ROUTES.receivedPayments.allowedSubscriptionPlans.includes(plan)
      : false,
    title: intl.formatMessage({ id: 'receivedPayments', defaultMessage: 'Obdržené platby' }),
    path: ROUTES.receivedPayments.path,
    icon: <PaymentsIcon />,
  },
  // {
  //   key: 'sms',
  //   title: intl.formatMessage({ id: 'sms', defaultMessage: 'SMSky' }),
  //   path: ROUTES.sms.path,
  //   icon: <SmsOutlinedIcon />,
  // },
  {
    key: 'shoppingList',
    hasFeatureAccess: plan ? ROUTES.shoppingList.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'shoppingList', defaultMessage: 'Nákupní seznam' }),
    path: ROUTES.shoppingList.path,
    icon: <ProductionQuantityLimitsIcon />,
  },
  {
    key: 'consumption',
    hasFeatureAccess: plan ? ROUTES.consumption.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'consumption', defaultMessage: 'Spotřeba' }),
    path: teamMember?.teamId ? Paths.consumption(teamMember.teamId) : '#',
    icon: <ContentCutIcon />,
    condition: () => !!teamMember,
  },
  {
    key: 'services',
    hasFeatureAccess: plan ? ROUTES.services.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'pricing', defaultMessage: 'Ceník' }),
    path: ROUTES.services.path,
    icon: <LocalOfferIcon />,
  },
  {
    key: 'stock',
    id: 'side-menu-button-stock',
    hasFeatureAccess: plan ? ROUTES.stock.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'stock', defaultMessage: 'Sklad' }),
    path: stocks?.[0]?.id ? Paths.stock(stocks[0].id) : '#',
    icon: <WarehouseIcon />,
    condition: () => !!stocks?.length,
  },
  {
    key: 'pricingCalculator',
    hasFeatureAccess: plan
      ? ROUTES.pricingCalculator.allowedSubscriptionPlans.includes(plan)
      : false,
    title: intl.formatMessage({ id: 'pricingCalculator', defaultMessage: 'Kalkulačka cen' }),
    path: ROUTES.pricingCalculator.path,
    icon: <CalculateIcon />,
  },

  {
    key: 'logs',
    hasFeatureAccess: plan ? ROUTES.logs.allowedSubscriptionPlans.includes(plan) : false,
    title: intl.formatMessage({ id: 'logs', defaultMessage: 'Záznamy o aktivitě' }),
    path: ROUTES.logs.path,
    icon: <LightbulbOutlineIcon />,
  },

  {
    key: 'usersOverview',
    hasFeatureAccess: userId === 'google-oauth2|113238590142888685973',
    title: intl.formatMessage({ id: 'usersOverview', defaultMessage: 'Přehled uživatelů' }),
    path: ROUTES.usersOverview.path,
    icon: <GroupIcon />,
  },
  {
    key: 'language',
    hasFeatureAccess: true,
    title: intl.formatMessage(
      { id: 'currentLanguage', defaultMessage: 'Language: {lang}' },
      { lang: filters?.language.toUpperCase() ?? 'CS' },
    ),
    path: undefined,
    icon: <LanguageIcon />,
    onClick: toggleLanguage,
  },
  {
    key: 'logout',
    hasFeatureAccess: true,

    path: undefined,
    title: intl.formatMessage({ id: 'logOut', defaultMessage: 'Odhlásit se' }),
    icon: <LogoutIcon />,
    onClick: logout,
  },
];
